import * as React from 'react';
import { Component } from 'react';
import { action, computed, toJS, observable } from 'mobx';
import { observer, inject } from 'mobx-react';
import get from 'lodash/get';
import isArray from 'lodash/isArray';
import groupBy from 'lodash/groupBy';
import FundCard from './FundCard';
import FundDocuments from './FundDocuments';
import getDataPoint from '../utils/getDataPoint';
import { performanceCheck as checkPerformance } from '../utils/performanceCheck';
import { sortString, sortNumber } from '../utils';
import { NO_DOCUMENTS_MESSAGE } from '../constants';
import { getDisclaimers } from '../utils/disclaimers';
import getConditionalData from '../utils/query/getConditionalData';
import getConditionalDataFlag from '../utils/query/getConditionalDataFlag';
import normalizeQueryResults from '../utils/query/normalizeQueryResults';
import { DataStore } from '../stores';
import { ShareClass } from '../models/entity';
import { isArrayLike } from '@kurtosys/ksys-app-template/dist/utils/isArrayLike';
import { IQueryContext } from '@kurtosys/ksys-api-client/dist/models/commonTypes';
import getQueryResults from '../utils/query/getQueryResults';
import { DocumentCategory } from '../models/configuration/documents/DocumentCategory';

// Higher-order component to return a shareclass row/card with data and methods required to function
const withShareclass = WrappedComponent => {
    @inject('dataStore')
    @observer
    class Shareclass extends Component<any, any> {
        @observable shareClassDetail: ShareClass;
        @observable shareClassDetailLoading: boolean = false;

        async componentDidMount() {
            const { dataStore } = this.props;
            // fetch initial fund card details
            if (dataStore.isExpanded) {
                await this.getShareClassDetail();
            }
        }

        async componentDidUpdate() {
            // fetch fund card details when tabs are changed
            await this.getShareClassDetail();
        }

        getShareClassDetail = async () => {
            const { shareClass } = this.props;
            // fetch fund card if expanded and it hasn't been fetched yet
            if (
                this.expanded &&
                shareClass &&
                (!this.shareClassDetail ||
                    this.shareClassDetail.clientCode !== shareClass.clientCode)
            ) {
                const { dataStore } = this.props;
                this.shareClassDetailLoading = true;
                const shareClassDetail = await dataStore.getFundDetail(
                    shareClass
                );
                this.shareClassDetail = shareClassDetail;
                this.shareClassDetailLoading = false;
            }
        };

        @computed
        get expanded() {
            const { shareClass, dataStore } = this.props;
            return dataStore.getExpandStatus(shareClass);
        }

        @computed
        get noDataPlaceholder() {
            return this.props.dataStore.config.noDataPlaceholder;
        }

        @computed
        get hasPerformance() {
            const { dataStore, shareClass } = this.props;
            const { config } = dataStore;
            return checkPerformance({
                dataStore,
                config,
                shareClass,
            });
        }

        @computed
        get selectedDocumentsLabel() {
            const { selectedDocumentsLabel } = this.props.dataStore.config;
            return selectedDocumentsLabel;
        }

        @computed
        get availableDocumentsLabel() {
            const { availableDocumentsLabel } = this.props.dataStore.config;
            return availableDocumentsLabel;
        }

        @computed
        get groupedDocuments() {
            const { documents } = this.props.shareClass;
            return this.groupAndSortDocuments(documents);
        }

        @computed
        get documentTypeAliases() {
            const { rowConfig, globalConfig } = this.props.dataStore.config;
            if (rowConfig && rowConfig.documentTypeAliases) {
                return rowConfig.documentTypeAliases;
            }
            if (
                globalConfig &&
                globalConfig.valueAliases &&
                globalConfig.valueAliases.documentTypes
            ) {
                return globalConfig.valueAliases.documentTypes;
            }
            return;
        }

        @computed
        get documentCategories() {
            const { rowConfig } = this.props.dataStore.config;
            const { documentCategories } = rowConfig;
            if (this.hasDocumentCategories) {
                const { dataStore } = this.props;
                const result = [];
                for (const documentCategory of documentCategories as Array<
                    DocumentCategory
                >) {
                    const documentCategoryQuery = {
                        heading: documentCategory.heading,
                        documentGroup: documentCategory.groupBy,
                    };
                    const documentData = getQueryResults(
                        this.queryContext,
                        documentCategoryQuery,
                        dataStore.culture
                    );
                    result.push({
                        heading: documentData.heading,
                        groupedDocuments: this.groupAndSortDocuments(
                            documentData.documentGroup
                        ),
                    });
                }
                return result;
            }
        }

        @computed
        get hasDocumentCategories() {
            const { rowConfig } = this.props.dataStore.config;
            const { documentCategories } = rowConfig;
            return documentCategories && documentCategories.length > 0;
        }

        @action
        groupAndSortDocuments = documents => {
            const {
                documentGroupMeta,
                cultureAliases,
                documentGroupOrder,
            } = this.props.dataStore.config;

            // get client codes to determine unique documents
            const clientCodes = documents.map(mapObj => mapObj.clientCode);
            // filter documents to bring back a unique list based on the clientCode
            const uniqueDocuments = documents.filter((obj, pos, arr) => {
                // indexOf returns the index of the first occurrence, using this logic,
                // we can filter out the duplicates that don't match the with the first occurrence therefore giving us a unique list.
                return clientCodes.indexOf(obj.clientCode) === pos;
            });

            const groupedDocuments = groupBy(
                uniqueDocuments,
                `meta.${documentGroupMeta}.value`
            );

            return Object.keys(groupedDocuments)
                .sort(sortString)
                .map(documentGroupName => {
                    const documentGroupNameIndex = documentGroupOrder.indexOf(
                        documentGroupName
                    );
                    return {
                        order:
                            documentGroupNameIndex > -1
                                ? documentGroupNameIndex
                                : documentGroupOrder.length,
                        documentGroupName,
                        documents: groupedDocuments[
                            documentGroupName
                        ].sort((a, b) =>
                            sortString(
                                get(
                                    cultureAliases,
                                    a.cultureCode,
                                    a.cultureCode
                                ),
                                get(
                                    cultureAliases,
                                    b.cultureCode,
                                    b.cultureCode
                                )
                            )
                        ),
                    };
                })
                .sort((a, b) => sortNumber(a.order, b.order));
        };

        isValueDisabled(
            dataStore: DataStore,
            shareClass: ShareClass,
            disabledEntityCheck: boolean
        ) {
            if (disabledEntityCheck) {
                const disabledEntityFlag = dataStore.config.disabledEntityFlag;

                if (
                    disabledEntityFlag &&
                    disabledEntityFlag.conditional &&
                    isArrayLike(disabledEntityFlag.queries) &&
                    disabledEntityFlag.queries.length > 0
                ) {
                    const values = normalizeQueryResults({
                        ...shareClass.properties_pub,
                        ...getConditionalData(shareClass, disabledEntityFlag),
                    });
                    return getConditionalDataFlag(values, disabledEntityFlag);
                }
            }
            return false;
        }

        @computed
        get columns() {
            const { dataStore, shareClass } = this.props;

            const values = this.props.columns.map(column => {
                const {
                    type,
                    property,
                    data,
                    columns,
                    groups,
                    disabledEntityCheck,
                } = column;

                const emptyResponse = col => ({
                    ...col,
                    data: {
                        value: col.noDataPlaceholder || this.noDataPlaceholder,
                    },
                });

                // GROUPED COLUMNS
                if (type === 'group' && columns.length > 0) {
                    return {
                        ...column,
                        values: columns.map(c => {
                            if (
                                this.isValueDisabled(
                                    dataStore,
                                    shareClass,
                                    c.disabledEntityCheck
                                )
                            ) {
                                return emptyResponse(c);
                            }

                            if (isArray(toJS(c.data))) {
                                return {
                                    ...c,
                                    multiple: true,
                                    data: c.data.map(d => ({
                                        ...d,
                                        value: getDataPoint({
                                            dataStore,
                                            shareClass,
                                            dataConfig: d,
                                            locale: this.locale,
                                            fundCurrency: this.fundCurrency,
                                            defaultNoDataPlaceholder:
                                                c.noDataPlaceholder ||
                                                this.noDataPlaceholder,
                                            hasPerformance: this.hasPerformance,
                                        }),
                                    })),
                                };
                            }

                            if (c.data) {
                                return {
                                    ...c,
                                    data: {
                                        ...c.data,
                                        value: getDataPoint({
                                            dataStore,
                                            shareClass,
                                            dataConfig: c.data,
                                            locale: this.locale,
                                            fundCurrency: this.fundCurrency,
                                            defaultNoDataPlaceholder:
                                                c.noDataPlaceholder ||
                                                this.noDataPlaceholder,
                                            hasPerformance: this.hasPerformance,
                                        }),
                                    },
                                };
                            }
                        }),
                    };
                }

                if (type === 'dropdown' && groups.length > 0) {
                    return {
                        ...column,
                        groups: groups.map(g => ({
                            ...g,
                            values: g.columns.map(c => {
                                if (
                                    this.isValueDisabled(
                                        dataStore,
                                        shareClass,
                                        c.disabledEntityCheck
                                    )
                                ) {
                                    return emptyResponse(c);
                                }

                                if (isArray(toJS(c.data))) {
                                    return {
                                        ...c,
                                        multiple: true,
                                        data: c.data.map(d => ({
                                            ...d,
                                            value: getDataPoint({
                                                dataStore,
                                                shareClass,
                                                dataConfig: d,
                                                locale: this.locale,
                                                fundCurrency: this.fundCurrency,
                                                defaultNoDataPlaceholder:
                                                    c.noDataPlaceholder ||
                                                    this.noDataPlaceholder,
                                                hasPerformance: this
                                                    .hasPerformance,
                                            }),
                                        })),
                                    };
                                }

                                if (c.data) {
                                    return {
                                        ...c,
                                        data: {
                                            ...c.data,
                                            value: getDataPoint({
                                                dataStore,
                                                shareClass,
                                                dataConfig: c.data,
                                                locale: this.locale,
                                                fundCurrency: this.fundCurrency,
                                                defaultNoDataPlaceholder:
                                                    c.noDataPlaceholder ||
                                                    this.noDataPlaceholder,
                                                hasPerformance: this
                                                    .hasPerformance,
                                            }),
                                        },
                                    };
                                }
                            }),
                        })),
                    };
                }

                // NON-GROUPED COLUMNS
                if (
                    this.isValueDisabled(
                        dataStore,
                        shareClass,
                        disabledEntityCheck
                    )
                ) {
                    return emptyResponse(column);
                }

                if (type === 'document') {
                    return {
                        ...column,
                        value: this.getDocuments(
                            column.documentType,
                            column.performanceCheck,
                            column.noDataPlaceholder
                        ),
                    };
                }

                if (isArray(toJS(data))) {
                    return {
                        ...column,
                        multiple: true,
                        data: data.map(d => {
                            return {
                                ...d,
                                value: getDataPoint({
                                    dataStore,
                                    shareClass,
                                    dataConfig: d,
                                    locale: this.locale,
                                    fundCurrency: this.fundCurrency,
                                    defaultNoDataPlaceholder:
                                        column.noDataPlaceholder ||
                                        this.noDataPlaceholder,
                                    hasPerformance: this.hasPerformance,
                                }),
                            };
                        }),
                    };
                }

                if (data) {
                    return {
                        ...column,
                        data: {
                            ...data,
                            value: getDataPoint({
                                dataStore,
                                shareClass,
                                dataConfig: data,
                                locale: this.locale,
                                fundCurrency: this.fundCurrency,
                                defaultNoDataPlaceholder:
                                    column.noDataPlaceholder ||
                                    this.noDataPlaceholder,
                                hasPerformance: this.hasPerformance,
                            }),
                        },
                    };
                }

                // Support simple label/property column definition
                if (property) {
                    return {
                        ...column,
                        data: {
                            ...data,
                            value: getDataPoint({
                                dataStore,
                                shareClass,
                                dataConfig: {
                                    property: 'properties_pub',
                                    code: property,
                                    key: 'value',
                                    performanceCheck: column.performanceCheck,
                                },
                                locale: this.locale,
                                fundCurrency: this.fundCurrency,
                                defaultNoDataPlaceholder:
                                    column.noDataPlaceholder ||
                                    this.noDataPlaceholder,
                                hasPerformance: this.hasPerformance,
                            }),
                        },
                    };
                }

                return emptyResponse(column);
            });

            return values;
        }

        getDocuments = (
            documentType,
            performanceCheck,
            noDataPlaceholder = this.noDataPlaceholder
        ) => {
            if (performanceCheck && !this.hasPerformance) {
                return noDataPlaceholder;
            }

            const { documents = [] } =
                this.groupedDocuments.find(
                    d => d.documentGroupName === documentType
                ) || {};

            return documents;
        };

        documentSelected = clientCode => {
            return (
                this.props.documentDownloads.findIndex(
                    document => document.clientCode === clientCode
                ) > -1
            );
        };

        @computed
        get shareClassDownloads() {
            const documents = [];
            if (this.hasDocumentCategories) {
                this.documentCategories.map(documentCategory => {
                    documentCategory.groupedDocuments.map(groupedDocument => {
                        documents.push(...groupedDocument.documents);
                    });
                });
            } else {
                this.groupedDocuments.map(groupedDocument => {
                    documents.push(...groupedDocument.documents);
                });
            }

            return documents.filter(document =>
                this.documentSelected(document.clientCode)
            );
        }

        @computed
        get downloadCount() {
            return this.shareClassDownloads.length;
        }

        @computed
        get watchListPropertyValue() {
            return get(
                this.props.shareClass,
                `properties_pub.${this.props.dataStore.config.watchListProperty}.value`
            );
        }

        @action
        toggleExpanded = () => {
            const { shareClass, dataStore } = this.props;
            dataStore.setExpandStatus(shareClass);
        };

        @action
        toggleWatchList = () => {
            this.props.dataStore.watchList.includes(this.watchListPropertyValue)
                ? this.removeWatchList()
                : this.addWatchList();
        };

        @action
        addWatchList = () => {
            const updatedWatchList = toJS(this.props.dataStore.watchList);
            updatedWatchList.push(this.watchListPropertyValue);
            this.props.dataStore.watchList = updatedWatchList;
        };

        @action
        removeWatchList = () => {
            const updatedWatchList = toJS(this.props.dataStore.watchList);
            this.props.dataStore.watchList = updatedWatchList.filter(
                value => value !== this.watchListPropertyValue
            );
        };

        @computed
        get inWatchList() {
            return this.props.dataStore.watchList.includes(
                this.watchListPropertyValue
            );
        }

        @computed
        get fundCurrency() {
            return get(
                this.props.shareClass,
                `properties_pub.${this.props.dataStore.config
                    .fundCurrencyProperty || 'ccy'}.value`
            );
        }

        @computed
        get locale() {
            return this.props.dataStore.culture;
        }

        @computed
        get fundCardDisclaimers() {
            return this.disclaimerByType(
                this.props.dataStore.config.rowConfig.disclaimers
            );
        }

        @computed
        get noDocumentsDisclaimers() {
            const {
                noDocumentsDisclaimers = [],
            } = this.props.dataStore.config.rowConfig;
            return noDocumentsDisclaimers.length > 0
                ? this.disclaimerByType(noDocumentsDisclaimers)
                : [NO_DOCUMENTS_MESSAGE];
        }

        @action
        disclaimerByType(disclaimerTypes) {
            const { disclaimers = {} } = this.props.shareClass;
            return getDisclaimers(
                this.props.dataStore,
                disclaimerTypes,
                disclaimers
            );
        }

        /**
         * queryContext is required to use IQueryOptions configurations
         *
         * @readonly
         */
        get queryContext(): IQueryContext {
            const { shareClass, dataStore } = this.props;
            return {
                entityByType: {
                    CLSS: shareClass,
                },
                globalDisclaimers: toJS(dataStore.disclaimers),
            };
        }

        render() {
            // TODO: Is there a DRYer way to get these as props on to the WrappedComponent?
            // Spreading {...this} doesn't work
            const {
                expanded,
                columns,
                shareClassDownloads,
                downloadCount,
                documentSelected,
                watchListPropertyValue,
                toggleExpanded,
                toggleWatchList,
                addWatchList,
                removeWatchList,
                inWatchList,
                hasPerformance,
                shareClassDetail,
                shareClassDetailLoading,
                queryContext,
            } = this;

            return (
                <WrappedComponent
                    {...this.props}
                    {...{
                        expanded,
                        columns,
                        shareClassDownloads,
                        downloadCount,
                        documentSelected,
                        watchListPropertyValue,
                        toggleExpanded,
                        toggleWatchList,
                        addWatchList,
                        removeWatchList,
                        inWatchList,
                        hasPerformance,
                        queryContext,
                    }}
                >
                    {this.props.rowType === 'FundCard' && (
                        <FundCard
                            isMobile={this.props.isMobile}
                            shareClass={shareClassDetail}
                            fundCardDisclaimers={this.fundCardDisclaimers}
                            groupedDocuments={this.groupedDocuments}
                            shareClassDetailLoading={shareClassDetailLoading}
                            queryContext={queryContext}
                        />
                    )}
                    {this.props.rowType === 'Documents' && (
                        <FundDocuments
                            groupedDocuments={this.groupedDocuments}
                            documentCategories={this.documentCategories}
                            documentDownloads={this.props.documentDownloads}
                            toggleDownload={this.props.toggleDownload}
                            documentSelected={this.documentSelected}
                            selectedDocumentsLabel={this.selectedDocumentsLabel}
                            availableDocumentsLabel={
                                this.availableDocumentsLabel
                            }
                            fundCardDisclaimers={this.fundCardDisclaimers}
                            noDocumentsDisclaimers={this.noDocumentsDisclaimers}
                            documentTypeAliases={this.documentTypeAliases}
                        />
                    )}
                </WrappedComponent>
            );
        }
    }

    return Shareclass;
};

export default withShareclass;



// WEBPACK FOOTER //
// ./src/Components/withShareclass.tsx